import React, { useContext, useEffect, useState } from 'react';
import {
  PieChart,
  BarChart,
  FileText,
  ArrowUpRight,
  Download,
  Calendar,
  Search,
  School,
  DollarSign,
  TrendingUp,
  Activity,
  Filter,
  ChevronDown,
  LayoutDashboard,
  BookOpen,
  RefreshCcw,
  PlusCircle,
  Trash2,
  Save,
  CheckCircle,
  Settings,
  Users,
  AlertCircle
} from 'lucide-react';
import { AppContext } from '../context/AppContext';
import { PostCallWithErrorResponse, simpleGetCallWithErrorResponse } from '../api/ApiServices';
import ApiConfig from '../api/ApiConfig';
import { ToastMsg } from './ChatAdmin/toastmsg';
import { setScriptKey } from '@progress/kendo-licensing';



// --- CSS Styles (Inlined for reliability) ---
const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

/* ==========================================================================
   1. VARIABLES & RESET
   ========================================================================== */
:root {
  --brand-primary: #0d6efd;
  --brand-bg: #f5f8ff;
  --brand-text: #212529;
  
  --ui-gray-bg: #F2F4F7;
  --ui-border: #d0d5dd;
  --ui-text-muted: #667085;
  --ui-text-dark: #344054;
  --radius-md: 0.5rem;

}

/* Scoped Reset for this component */
.ds-layout-row * { box-sizing: border-box; }
.ds-layout-row {

}

/* ==========================================================================
   2. BOOTSTRAP REPLACEMENT UTILITIES (No Import Needed)
   ========================================================================== */
/* Flexbox */
.d-flex { display: flex !important; }
.align-items-center { align-items: center !important; }
.justify-content-center { justify-content: center !important; }
.justify-content-between { justify-content: space-between !important; }
.gap-2 { gap: 0.5rem !important; }
.gap-3 { gap: 1rem !important; }

/* Spacing */
.p-1 { padding: 0.25rem !important; }
.mb-0 { margin-bottom: 0 !important; }
.mb-1 { margin-bottom: 0.25rem !important; }
.mb-4 { margin-bottom: 1.5rem !important; }
.ps-5 { padding-left: 3rem !important; }
.ms-3 { margin-left: 1rem !important; }

/* Text & Colors */
.text-center { text-align: center !important; }
.text-end { text-align: right !important; }
.fw-bold { font-weight: 700 !important; }
.text-white { color: #fff !important; }
.text-dark { color: #101828 !important; }
.text-muted { color: var(--ui-text-muted) !important; }
.text-primary { color: var(--brand-primary) !important; }
.bg-primary { background-color: var(--brand-primary) !important; }
.bg-info { background-color: #0dcaf0 !important; }
.bg-opacity-25 { --bs-bg-opacity: 0.25; background-color: rgba(13, 202, 240, 0.25) !important; }

/* Display */
.d-none { display: none !important; }
@media (min-width: 768px) { .d-md-block { display: block !important; } }

/* Formatting */
.rounded { border-radius: var(--radius-md) !important; }
.rounded-circle { border-radius: 50% !important; }
.lh-1 { line-height: 1 !important; }

/* Position */
.position-relative { position: relative !important; }
.position-absolute { position: absolute !important; }
.top-50 { top: 50% !important; }
.start-0 { left: 0 !important; }
.translate-middle-y { transform: translateY(-50%) !important; }

/* Font Sizes */
.text-xs { font-size: 0.75rem; }
.text-sm { font-size: 0.875rem; }
.text-xl { font-size: 1.25rem; font-weight: 600; }

/* ==========================================================================
   3. LAYOUT COMPONENTS
   ========================================================================== */
.ds-sidebar {
  width: var(--sidebar-width);
  background: #fff;
  border-right: 1px solid var(--ui-border);
  display: flex; flex-direction: column; flex-shrink: 0;
  z-index: 20;
}
.ds-sidebar-header {
  height: 64px; display: flex; align-items: center; padding: 0 1.5rem;
  border-bottom: 1px solid var(--ui-gray-bg);
}
.ds-sidebar-content { padding: 1rem; overflow-y: auto; flex: 1; }

.ds-main-column {
  flex: 1; display: flex; flex-direction: column; overflow: hidden;
  background-color: var(--brand-bg); min-width: 0;
}
.ds-topbar {
  height: 64px; background: #fff; border-bottom: 1px solid var(--ui-border);
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 2rem; flex-shrink: 0;
}
.ds-scrollable-content { flex: 1; overflow-y: auto; padding: 2rem; }

/* Navigation */
.nav-group-label {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  color: var(--ui-text-muted); margin: 1.5rem 0 0.5rem 0.75rem;
}
.nav-item {
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0.625rem 0.75rem; border-radius: var(--radius-md);
  color: var(--ui-text-dark); font-weight: 500; font-size: 0.875rem;
  cursor: pointer; transition: all 0.2s; margin-bottom: 2px;
}
.nav-item:hover { background-color: var(--ui-gray-bg); color: var(--brand-text); }
.nav-item.active { background-color: var(--color-info-bg); color: var(--brand-primary); }
.nav-item-sub { padding-left: 2.25rem; font-size: 0.85rem; }

/* ==========================================================================
   4. TABS & FORM ELEMENTS
   ========================================================================== */
.ds-tabs-header {
  display: flex; gap: 2rem; border-bottom: 1px solid var(--ui-border);
  margin-bottom: 1.5rem; padding-bottom: 1px;
}
.ds-tab-item {
  padding: 0 0 0.75rem 0; color: var(--ui-text-muted); font-weight: 500;
  font-size: 0.9rem; cursor: pointer; border-bottom: 2px solid transparent;
  transition: all 0.2s; margin-bottom: -1px;
  
}
.ds-tab-item:hover { color: var(--brand-text); border-bottom-color: var(--ui-border); }
.ds-tab-item.active { color:var(--blue_53B7E8_LightMode) !important; border-bottom-color: var(--blue_53B7E8_LightMode) !important; }

.btn {
  font-weight: 600; padding: 0.5rem 1rem; border-radius: var(--radius-md);
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  font-size: 13px; cursor: pointer; border: 1px solid transparent;
}
.btn-primary { background-color: var(--brand-primary); color: #fff; }
.btn-primary:hover { background-color: #0b5ed7; }

.form-control {
  width: 100%; font-size: 14px; border-radius: 8px; border: 1px solid var(--ui-border);
  padding: 0.375rem 0.75rem; background: var(--ui-gray-bg); outline: none;
}
.date-range-group {
    display: flex; align-items: center; background: #fff;
    border: 1px solid var(--ui-border); border-radius: var(--radius-md);
    padding: 0.25rem 0.5rem; gap: 0.5rem;
}
.date-input-clean {
    border: none; font-size: 13px; font-weight: 500;
    color: var(--ui-text-dark); outline: none; background: transparent;
    font-family: 'Inter', sans-serif;
}

@media print {
    .ds-sidebar, .ds-topbar, .no-print, .ds-tabs-header { display: none !important; }
    .ds-main-column, .ds-scrollable-content { overflow: visible !important; height: auto !important; display: block !important; padding: 0 !important; }
}
`;


// --- Mock Data ---
const REPORT_DATA = {
  balanceSheet: {
    assets: [
      {
        category: "Current Assets", items: [
          { name: "Cash & Bank Balances", value: 1250000 },
          { name: "Tuition Receivable", value: 340000 },
          { name: "Inventory (Books & Uniforms)", value: 85000 },
          { name: "Prepaid Expenses", value: 25000 },
        ]
      },
      {
        category: "Fixed Assets", items: [
          { name: "School Buildings & Land", value: 4500000 },
          { name: "Furniture & Fixtures", value: 320000 },
          { name: "School Buses / Transportation", value: 450000 },
          { name: "Computer Lab Equipment", value: 150000 },
          { name: "Less: Accumulated Depreciation", value: -650000 },
        ]
      }
    ],
    liabilities: [
      {
        category: "Current Liabilities", items: [
          { name: "Accounts Payable", value: 110000 },
          { name: "Deferred Tuition Revenue (Term 2)", value: 850000 },
          { name: "Accrued Staff Salaries", value: 180000 },
          { name: "Student Caution Deposits", value: 50000 },
        ]
      },
      {
        category: "Long Term Liabilities", items: [
          { name: "Bank Loan (Campus Expansion)", value: 1200000 },
        ]
      }
    ],
    equity: [
      {
        category: "Equity", items: [
          { name: "School Corpus Fund", value: 3000000 },
          { name: "Retained Earnings", value: 730000 },
        ]
      }
    ]
  },
  profitAndLoss: {
    revenue: [
      {
        category: "Academic Revenue", items: [
          { name: "Tuition Fees", value: 3200000 },
          { name: "Admission / Registration Fees", value: 150000 },
          { name: "Examination Fees", value: 80000 },
        ]
      },
      {
        category: "Auxiliary Revenue", items: [
          { name: "Transportation / Bus Fees", value: 420000 },
          { name: "Canteen / Cafeteria Sales", value: 180000 },
          { name: "Uniform & Book Sales", value: 210000 },
          { name: "Facility Rentals (Weekend)", value: 45000 },
        ]
      }
    ],
    expenses: [
      {
        category: "Personnel Costs", items: [
          { name: "Teaching Staff Salaries", value: 1650000 },
          { name: "Admin & Support Staff Salaries", value: 450000 },
          { name: "Staff Benefits & Training", value: 120000 },
        ]
      },
      {
        category: "Operational Costs", items: [
          { name: "Utilities (Power, Water, Internet)", value: 180000 },
          { name: "Facility Maintenance & Repairs", value: 140000 },
          { name: "Transportation Fuel & Maintenance", value: 160000 },
          { name: "Canteen Supplies", value: 110000 },
          { name: "Classroom Supplies & Software", value: 95000 },
        ]
      },
      {
        category: "Admin & Marketing", items: [
          { name: "Marketing & Admissions", value: 120000 },
          { name: "Legal & Professional Fees", value: 40000 },
          { name: "Depreciation Expense", value: 110000 },
        ]
      }
    ]
  },
  cashFlow: {
    operating: [
      { name: "Net Income", value: 1110000 },
      { name: "Adjustments for Depreciation", value: 110000 },
      { name: "Decrease in Tuition Receivable", value: 45000 },
      { name: "Increase in Deferred Revenue", value: 200000 },
      { name: "Decrease in Accounts Payable", value: -30000 },
    ],
    investing: [
      { name: "Purchase of New School Bus", value: -120000 },
      { name: "Upgrade of Science Lab", value: -50000 },
    ],
    financing: [
      { name: "Repayment of Bank Loan", value: -100000 },
      { name: "Dividend/Distribution to Owners", value: -200000 },
    ]
  },
  trialBalance: [
    { code: "1010", account: "Cash - Operating", debit: 850000, credit: 0 },
    { code: "1020", account: "Cash - Savings", debit: 400000, credit: 0 },
    { code: "1100", account: "Tuition Receivable", debit: 340000, credit: 0 },
    { code: "1200", account: "Inventory - Uniforms", debit: 85000, credit: 0 },
    { code: "1500", account: "Land & Buildings", debit: 4500000, credit: 0 },
    { code: "1600", account: "School Buses", debit: 450000, credit: 0 },
    { code: "2000", account: "Accounts Payable", debit: 0, credit: 110000 },
    { code: "2100", account: "Deferred Tuition", debit: 0, credit: 850000 },
    { code: "2200", account: "Bank Loan", debit: 0, credit: 1200000 },
    { code: "3000", account: "School Corpus", debit: 0, credit: 3000000 },
    { code: "4000", account: "Tuition Revenue", debit: 0, credit: 3200000 },
    { code: "4100", account: "Bus Fee Revenue", debit: 0, credit: 420000 },
    { code: "5000", account: "Salaries Expense", debit: 2100000, credit: 0 },
    { code: "5100", account: "Utilities Expense", debit: 180000, credit: 0 },
  ],
  accounts: [
    { code: "1010", name: "Cash - Operating" },
    { code: "4000", name: "Tuition Revenue" },
    { code: "5000", name: "Salaries Expense" },
    { code: "5100", name: "Utilities Expense" },
    { code: "6100", name: "Office Supplies" },
    { code: "2000", name: "Accounts Payable" }
  ]
};


// --- Helper Functions ---
const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);


const SectionHeader = ({ title, total, type = "neutral" }) => {
  let textColor = 'text-dark';
  if (type === 'positive') textColor = 'text-success';
  if (type === 'negative') textColor = 'text-danger';


  return (
    <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-light border-bottom mt-4 rounded-top">
      <h6 className="mb-0 fw-bold text-muted text-sm text-uppercase">{title}</h6>
      <span className={`fw-bold font-monospace ${textColor}`}>
        {total}
      </span>
    </div>
  );
};


const LineItem = ({ name, value, indent = false }) => (
  <div className={`d-flex justify-content-between align-items-center py-2 px-3 border-bottom border-light ${indent ? 'ps-4' : ''}`}>
    <span className="text-sm text-dark font-medium">{name}</span>
    <span className="font-monospace text-sm text-muted">{value}</span>
  </div>
);


// --- Report Components ---


const BalanceSheet = ({ dateRange }) => {
  const data = REPORT_DATA.balanceSheet;
  const totalAssets = data.assets.reduce((acc, cat) => acc + cat.items.reduce((s, i) => s + i.value, 0), 0);
  const totalLiabilities = data.liabilities.reduce((acc, cat) => acc + cat.items.reduce((s, i) => s + i.value, 0), 0);
  const totalEquity = data.equity.reduce((acc, cat) => acc + cat.items.reduce((s, i) => s + i.value, 0), 0);
  const [balanceSheet, setBalanceSheet] = useState({})
  // Check if the balance sheet is balanced
  const isBalanced = balanceSheet?.TotalAssets === balanceSheet?.TotalLiabilities + balanceSheet?.TotalEquity;




  const getBalanceSheet = () => {

    PostCallWithErrorResponse(ApiConfig.GET_BALANCE_SHEET, JSON.stringify({})).then((res) => {


      if (res?.json?.Success) {
        setBalanceSheet(res?.json?.Data)
      }


    })

  }

  useEffect(() => {

    getBalanceSheet()

  }, [])


  return (
    <div className="card-std" style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '12px' }}>
      <div className="d-flex justify-content-between align-items-end mb-4 border-bottom pb-3">
        <div>
          <h4 className="mb-1">Statement of Financial Position</h4>
          <div className="text-muted text-sm">Period Ending: {dateRange.end}</div>
        </div>
        <div className="text-end">
          <span
            className={`badge rounded-pill ${isBalanced ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}
            style={{ padding: '8px 16px', fontSize: '0.85rem' }}
          >
            {isBalanced ? "● Balanced" : "● Unbalanced"}
          </span>
        </div>
      </div>

      <div className="row g-4">
        {/* Assets Section - Full Width Header Style */}
        <div className="col-md-12">
          <div
            className="p-3 rounded-3 mb-3 d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#eef2ff', border: '1px solid #c3dafe' }} // Light Indigo/Blue
          >
            <span className="fw-bold text-primary">Total Assets</span>
            <span className="fw-bold text-primary fs-5">{balanceSheet?.TotalAssets}</span>
          </div>
          <div style={{ padding: '0 10px' }}>
            {balanceSheet?.AssetGroups?.map((category, idx) => (
              <div key={idx} className="mb-3">
                <SectionHeader title={category?.GroupName} total={category?.TotalAmount} />
                {category?.LineItems?.map((item, i) => <LineItem key={i} name={item?.LedgerName} value={item?.Amount} indent />)}
              </div>
            ))}
          </div>
        </div>

        {/* Liabilities Section */}
        <div className="col-md-6">
          <div
            className="p-3 rounded-3 mb-3 d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2' }} // Light Red
          >
            <span className="fw-bold text-danger">Total Liabilities</span>
            <span className="fw-bold text-danger fs-5">{balanceSheet?.TotalLiabilities}</span>
          </div>
          <div className="p-2" style={{ backgroundColor: '#fafafa', borderRadius: '8px' }}>
            {balanceSheet?.LiabilityGroups?.map((category, idx) => (
              <div key={idx} className="mb-2">
                <SectionHeader title={category?.GroupName} total={category?.TotalAmount} />
                {category?.LineItems?.map((item, i) => <LineItem key={i} name={item?.LedgerName} value={item?.Amount} indent />)}
              </div>
            ))}
          </div>
        </div>

        {/* Equity Section */}
        <div className="col-md-6">
          <div
            className="p-3 rounded-3 mb-3 d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }} // Light Green
          >
            <span className="fw-bold text-success">Total Equity</span>
            <span className="fw-bold text-success fs-5">{balanceSheet?.TotalEquity}</span>
          </div>
          <div className="p-2" style={{ backgroundColor: '#fafafa', borderRadius: '8px' }}>
            {balanceSheet?.EquityGroups?.map((category, idx) => (
              <div key={idx} className="mb-2">
                <SectionHeader title={category?.GroupName} total={category?.TotalAmount} />
                {category?.LineItems?.map((item, i) => <LineItem key={i} name={item?.LedgerName} value={item?.Amount} indent />)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Balancing Footer Note */}
      <div
        className="mt-4 p-3 text-center rounded-3 text-sm"
        style={{
          backgroundColor: isBalanced ? '#f8f9fa' : '#fff5f5',
          border: `1px dashed ${isBalanced ? '#dee2e6' : '#fc8181'}`,
          color: isBalanced ? '#6c757d' : '#e53e3e'
        }}
      >
        {isBalanced
          ? `Assets (${balanceSheet?.TotalAssets}) equals Liabilities + Equity (${balanceSheet?.TotalLiabilities + balanceSheet?.TotalEquity})`
          : `Warning: Balance sheet is out of balance by ${Math.abs(balanceSheet?.TotalAssets - (balanceSheet?.TotalLiabilities + balanceSheet?.TotalEquity))}`
        }
      </div>
    </div>
  );
};


const ProfitAndLoss = ({ dateRange }) => {
  const data = REPORT_DATA.profitAndLoss;
  const totalRevenue = data.revenue.reduce((acc, cat) => acc + cat.items.reduce((s, i) => s + i.value, 0), 0);
  const totalExpenses = data.expenses.reduce((acc, cat) => acc + cat.items.reduce((s, i) => s + i.value, 0), 0);
  const netIncome = totalRevenue - totalExpenses;

  const [report, setReport] = useState({})


  const getPlReport = () => {

    PostCallWithErrorResponse(ApiConfig.GET_PROFIT_AND_LOST_STATEMENT, JSON.stringify({})).then((res) => {

      if (res?.json?.Success)
        setReport(res?.json?.Data)
    })

  }

  useEffect(() => {
    getPlReport()
  }, [])


  return (
    <div className="taskMain" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
      <div className="row g-3 mb-4 empolyDetails">
        {/* Revenue Card */}
        <div className="col-md-4">
          <div
            className="card-std card-interactive h-100 p-3"
            style={{ backgroundColor: '#e6fffa', border: '1px solid #b2f5ea' }}
          >
            <div className="text-muted text-xs fw-bold text-uppercase">Total Revenue</div>
            <div className="text-2xl fw-bold text-success mt-1">{report?.TotalRevenue}</div>
            <div className="text-xs text-success mt-2 d-inline-block px-2 py-1 bg-success-subtle rounded">+12% vs last year</div>
          </div>
        </div>

        {/* Expenses Card */}
        <div className="col-md-4">
          <div
            className="card-std card-interactive h-100 p-3"
            style={{ backgroundColor: '#fff5f5', border: '1px solid #feb2b2' }}
          >
            <div className="text-muted text-xs fw-bold text-uppercase">Total Expenses</div>
            <div className="text-2xl fw-bold text-danger mt-1">{report?.TotalExpenses}</div>
            <div className="text-xs text-danger mt-2 d-inline-block px-2 py-1 bg-danger-subtle rounded">+5% vs last year</div>
          </div>
        </div>

        {/* Net Income Card */}
        <div className="col-md-4">
          <div
            className="card-std card-interactive h-100 p-3"
            style={{ backgroundColor: '#ebf8ff', border: '1px solid #bee3f8' }}
          >
            <div className="text-muted text-xs fw-bold text-uppercase">Net Income</div>
            <div className="text-2xl fw-bold text-primary mt-1">{report?.NetIncome}</div>
            <div className="text-xs text-primary mt-2 d-inline-block px-2 py-1 bg-primary-subtle rounded">
              Margin: {((report?.NetIncome / report?.TotalRevenue) * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* Main Statement Card */}
      <div className="card-std" style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <div className="d-flex justify-content-between align-items-end mb-4 border-bottom pb-3 p-4">
          <div>
            <h4 className="mb-1">Statement of Activities</h4>
            <div className="text-muted text-sm">Period: {dateRange.start} to {dateRange.end}</div>
          </div>
        </div>

        <div className="row g-5 p-4">
          <div className="col-md-6">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Revenues</h5>
              {/* <span className="badge bg-success-subtle text-success">{formatCurrency(totalRevenue)}</span> */}
            </div>
            {report?.RevenueGroups?.map((category, idx) => (
              <div key={idx} className="mb-4">
                <h6 className="text-muted text-xs fw-bold text-uppercase ps-2 border-start border-3 border-success mb-2">{category?.GroupName}</h6>
                {category?.LineItems?.map((item, i) => <LineItem key={i} name={item.LedgerName} value={item.Amount} />)}
              </div>
            ))}
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Expenses</h5>
              {/* <span className="badge bg-danger-subtle text-danger">{formatCurrency(totalExpenses)}</span> */}
            </div>
            {report?.ExpenseGroups?.map((category, idx) => (
              <div key={idx} className="mb-4">
                <h6 className="text-muted text-xs fw-bold text-uppercase ps-2 border-start border-3 border-danger mb-2">{category?.GroupName}</h6>
                {category?.LineItems?.map((item, i) => <LineItem key={i} name={item.LedgerName} value={item.Amount} />)}
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-4 p-4 rounded-3 d-flex justify-content-between align-items-center"
          style={{ backgroundColor: report?.NetIncome >= 0 ? '#f0fff4' : '#fff5f5', margin: '0 20px 20px 20px' }}
        >
          <span className="text-lg fw-bold text-dark">Net Surplus / (Deficit)</span>
          <span className={`text-xl fw-bold ${report?.NetIncome >= 0 ? 'text-success' : 'text-danger'}`}>
            {report?.NetIncome}
          </span>
        </div>
      </div>
    </div>
  );
};



const CashFlow = ({ dateRange }) => {
  const data = REPORT_DATA.cashFlow;
  const calcTotal = (items) => items.reduce((acc, item) => acc + item.value, 0);
  const netCash = calcTotal(data.operating) + calcTotal(data.investing) + calcTotal(data.financing);

  const ActivitySection = ({ title, items, icon: Icon, colorClass, bgColor, borderColor, iconColor }) => (
    <div className="card-std p-0 overflow-hidden mb-4" style={{ backgroundColor: '#ffffff', border: `1px solid ${borderColor}` }}>
      {/* Section Header */}
      <div
        className={`p-3 border-bottom d-flex align-items-center gap-2`}
        style={{ backgroundColor: bgColor }}
      >
        <Icon size={18} style={{ color: iconColor }} />
        <h6 className={`mb-0 fw-bold`} style={{ color: iconColor }}>{title}</h6>
      </div>

      {/* Line Items */}
      <div className="p-0">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="d-flex justify-content-between align-items-center p-3 border-bottom border-light"
            style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa' }} // Subtle zebra stripe
          >
            <span className="text-sm text-dark font-medium">{item.name}</span>
            <span className={`font-monospace text-sm fw-bold ${item.value < 0 ? 'text-danger' : 'text-success'}`}>
              {item.value < 0 ? `(${formatCurrency(Math.abs(item.value))})` : formatCurrency(item.value)}
            </span>
          </div>
        ))}
      </div>

      {/* Section Footer Summary */}
      <div className="p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
        <span className="text-xs fw-bold text-muted text-uppercase">Net Cash from {title.split(' ')[0]}</span>
        <span className={`fw-bold font-monospace ${calcTotal(items) < 0 ? 'text-danger' : 'text-success'}`}>
          {formatCurrency(calcTotal(items))}
        </span>
      </div>
    </div>
  );

  return (
    <div>
      {/* Header Summary Card */}
      <div
        className="card-std mb-4 text-white"
        style={{
          backgroundColor: 'var(--blue_53B7E8_LightMode) !important',
          border: '1px solid var(--blue_53B7E8_LightMode) ',
          padding: '24px',
          borderRadius: '12px'
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold mb-0 text-white">Cash Flows</h4>
            <p className="text-white-50 text-sm mb-0">Indirect Method ({dateRange.start} - {dateRange.end})</p>
          </div>
          <div className="text-end">
            <div className="text-white-50 text-xs text-uppercase fw-bold">Net Change in Cash</div>
            <div className="text-3xl fw-bold">
              {formatCurrency(netCash)}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Sections */}
      <div className="row">
        <div className="col-md-12">
          <ActivitySection
            title="Operating Activities"
            items={data.operating}
            icon={Activity}
            bgColor="#eef2ff"
            borderColor="#c3dafe"
            iconColor="#4338ca"
          />
        </div>
        <div className="col-md-6">
          <ActivitySection
            title="Investing Activities"
            items={data.investing}
            icon={TrendingUp}
            bgColor="#e0f7fa"
            borderColor="#b2ebf2"
            iconColor="#00acc1"
          />
        </div>
        <div className="col-md-6">
          <ActivitySection
            title="Financing Activities"
            items={data.financing}
            icon={DollarSign}
            bgColor="#fff8e1"
            borderColor="#ffecb3"
            iconColor="#f57c00"
          />
        </div>
      </div>

      {/* Reconciliation Card */}
      <div className="card-std" style={{ backgroundColor: '#ffffff', padding: '20px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <div className="d-flex justify-content-between py-2 text-muted">
          <span className="text-sm fw-medium">Cash at Beginning of Period</span>
          <span className="font-monospace fw-bold"> {formatCurrency(139000)}</span>
        </div>
        <div
          className="d-flex justify-content-between py-3 text-dark fw-bold border-top mt-2"
          style={{ fontSize: '1.1rem' }}
        >
          <span>Cash at End of Period</span>
          <span className="font-monospace" style={{ color: '#0d6efd' }}>{formatCurrency(139000 + netCash)}</span>
        </div>
      </div>
    </div>
  );
};


const TrialBalance = ({ dateRange }) => {
  const [filterText, setFilterText] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const rawData = REPORT_DATA.trialBalance;

  const [trialBalance, setTrialBalance] = useState({})

  const data = trialBalance?.Accounts?.filter(row =>

    row.AccountName?.toLowerCase().includes(filterText?.toLowerCase())
  );

  const totalDebit = data?.reduce((sum, item) => sum + item.DebitAmount, 0);
  const totalCredit = data?.reduce((sum, item) => sum + item.CreditAmount, 0);
  const isOutOfBalance = totalDebit !== totalCredit;

  const getTrialBalance = () => {

    PostCallWithErrorResponse(ApiConfig.GET_TRIAL_BALANCE, JSON.stringify({})).then((res) => {


      if (res?.json?.Success) {

        setTrialBalance(res?.json?.Data)
      }

    })

  }


  useEffect(() => {

    getTrialBalance()


  }, [])

  return (
    <div
      className="card-std p-0 overflow-hidden"
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}
    >
      {/* Header Section */}
      <div
        className="p-4 d-flex justify-content-between align-items-center"
        style={{ borderBottom: '2px solid #f1f5f9' }}
      >
        <div>
          <h4 className="mb-1" style={{ color: '#1e293b', fontWeight: '700' }}>Trial Balance</h4>
          <p className="text-muted text-sm mb-0">Unadjusted • As of {dateRange.end}</p>
        </div>
        <div className="no-print d-flex align-items-center gap-2">
          {showFilter && (
            <div className="position-relative">
              <Search size={14} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
              <input
                type="text"
                className="form-control ps-4 py-1 text-sm"
                style={{ borderRadius: '6px', border: '1px solid #cbd5e1' }}
                placeholder="Search account..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <button
            className={`btn d-flex align-items-center gap-2`}
            style={{
              backgroundColor: showFilter ? '#3b82f6' : '#ffffff',
              color: showFilter ? '#ffffff' : '#64748b',
              border: '1px solid #cbd5e1',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
            onClick={() => {
              setShowFilter(!showFilter);
              if (showFilter) setFilterText('');
            }}
          >
            <Filter size={14} /> {showFilter ? 'Hide Filter' : 'Filter View'}
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover mb-0 align-middle">
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th className="p-3 text-muted text-xs fw-bold text-uppercase border-bottom-0">Acct Code</th>
              <th className="p-3 text-muted text-xs fw-bold text-uppercase border-bottom-0">Account Name</th>
              <th className="p-3 text-muted text-xs fw-bold text-uppercase text-end border-bottom-0">Debit</th>
              <th className="p-3 text-muted text-xs fw-bold text-uppercase text-end border-bottom-0">Credit</th>
            </tr>
          </thead>
          <tbody>
            {data?.length > 0 ? (
              data?.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td className="p-3 text-muted font-monospace text-sm">{row?.AccountCode}</td>
                  <td className="p-3 fw-medium text-dark text-sm">{row?.AccountName}</td>
                  <td className="p-3 text-end font-monospace text-secondary text-sm">
                    {row?.DebitAmount > 0 ? row?.DebitAmount : '—'}
                  </td>
                  <td className="p-3 text-end font-monospace text-secondary text-sm">
                    {row?.CreditAmount > 0 ? row?.CreditAmount : '—'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="p-5 text-center text-muted text-sm" style={{ backgroundColor: '#fafafa' }}>
                  No accounts found matching "<strong>{filterText}</strong>"
                </td>
              </tr>
            )}
          </tbody>
          <tfoot
            className="fw-bold"
            style={{
              backgroundColor: isOutOfBalance ? '#fff1f2' : '#f8fafc',
              borderTop: '2px solid #e2e8f0'
            }}
          >
            <tr>
              <td className="p-3 text-dark text-sm" colSpan={2}>
                Totals {filterText && <span style={{ color: '#3b82f6', marginLeft: '5px' }}>(Filtered View)</span>}
              </td>
              <td
                className="p-3 text-end font-monospace text-sm"
                style={{ color: isOutOfBalance ? '#e11d48' : '#1e293b' }}
              >
                {totalDebit}
              </td>
              <td
                className="p-3 text-end font-monospace text-sm"
                style={{ color: isOutOfBalance ? '#e11d48' : '#1e293b' }}
              >
                {totalCredit}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {isOutOfBalance && (
        <div
          className="p-2 text-center text-xs fw-bold text-uppercase"
          style={{ backgroundColor: '#e11d48', color: '#ffffff' }}
        >
          Warning: Trial Balance is not equal (Difference: {totalDebit - totalCredit})
        </div>
      )}
    </div>
  );
};


// --- Component: Manual Ledger Entry ---




const ManualLedgerForm = ({ lederList }) => {

  const initialRows = [
    { LedgerId: '', debit: '', credit: '', LineMemo: '' },
    { LedgerId: '', debit: '', credit: '', LineMemo: '' }
  ]

  const initialHeader = { PostingDate: new Date().toISOString().split('T')[0], ReferenceNo: '', Description: '' }

  const [rows, setRows] = useState(initialRows);
  const [header, setHeader] = useState(initialHeader);



  const [isValid, setIsValid] = useState(true)



  const addRow = () => {
    setRows([...rows, { LedgerId: '', debit: '', credit: '', LineMemo: '' },]);
  };

  const removeRow = (id) => {
    if (rows.length > 2) {
      setRows(rows.filter(r => r.id !== id));
    }
  };

  const updateRow = (index, field, value) => {
    setRows(prevRows =>
      prevRows.map((r, idx) =>
        idx === index ? { ...r, [field]: value } : r
      )
    );
  };

  const totalDebit = rows.reduce((sum, r) => sum + (parseFloat(r.debit) || 0), 0);
  const totalCredit = rows.reduce((sum, r) => sum + (parseFloat(r.credit) || 0), 0);
  const difference = totalDebit - totalCredit;
  const isBalanced = Math.abs(difference) < 0.01 && totalDebit > 0;


  const handleSubmit = () => {

    let isValid = true;

    if (!header?.ReferenceNo || !header?.Description) {

      isValid = false


    }

    rows?.forEach((i) => {

      if (!i?.LedgerId || !i?.LineMemo?.length) {

        isValid = false

      }

    })


    setIsValid(isValid)

    if (isValid) {

      let payload = {
        ...header,
        Lines: rows
      }

      PostCallWithErrorResponse(ApiConfig.CREATE_MANUAL_LEDGER, JSON.stringify(payload)).then((res) => {


        if (res?.json?.Success) {
          setRows(initialRows)
          setHeader(initialHeader)
          ToastMsg('success', res?.json?.Message)
        } else {
          ToastMsg('error', res?.json?.Message)
        }


      })

    }




  }


  return (
    <div className="card-std" style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
      {/* Header Title Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
        <div>
          <h4 className="mb-1" style={{ color: '#1e293b' }}>Create Manual Journal Entry</h4>
          <p className="text-muted text-sm mb-0">Record adjustments, transfers, or non-system transactions.</p>
        </div>
        <div className="text-end">
          <span className="badge me-2" style={{ backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}>Draft Status</span>
          <span className="text-xs text-muted d-block mt-1">JE #{new Date().getFullYear()}-0042</span>
        </div>
      </div>

      {/* Form Metadata Section */}
      <div className="row g-3 mb-4 p-3 rounded-3" style={{ backgroundColor: '#f8fafc', border: '1px solid #f1f5f9' }}>
        <div className="col-md-3">
          <label className="form-label fw-bold text-xs text-uppercase text-muted">Posting Date</label>
          <input type="date" className="form-control form-control-sm" value={header.PostingDate} onChange={e => setHeader({ ...header, PostingDate: e.target.value })} />
        </div>
        <div className="col-md-3">
          <label className="form-label fw-bold text-xs text-uppercase text-muted">Reference / Slip #</label>
          <input type="text" className="form-control form-control-sm" placeholder="e.g. ADJ-OCT-01"
            value={header.ReferenceNo} onChange={e => setHeader({ ...header, ReferenceNo: e.target.value })}
            style={{
              border: !isValid && !header.ReferenceNo
                ? "1px solid red"
                : ""
            }}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label fw-bold text-xs text-uppercase text-muted">Journal Description</label>
          <input type="text" className="form-control form-control-sm"
            placeholder="Reason for this entry..." value={header.Description}
            onChange={e => setHeader({ ...header, Description: e.target.value })}
            style={{
              border: !isValid && !header.Description
                ? "1px solid red"
                : ""
            }}
          />
        </div>
      </div>

      {/* Ledger Table */}
      <div className="table-responsive mb-3">
        <table className="table table-bordered align-middle">
          <thead style={{ backgroundColor: '#f8fafc' }}>
            <tr className="text-muted text-xs text-uppercase">
              <th style={{ width: '35%', padding: '12px' }}>Account</th>
              <th style={{ width: '15%' }}>Debit</th>
              <th style={{ width: '15%' }}>Credit</th>
              <th>Line Memo</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td className="p-2">

                  <select className={`form-select form-select-sm  bg-light
                  ${!isValid && !row.LedgerId ? "border border-danger" : ""
                    }
                  `}

                    value={row.LedgerId}
                    onChange={e => updateRow(index, 'LedgerId', Number(e.target.value))}>
                    <option value="">Select Account...</option>
                    {lederList?.map(acc => (
                      <option key={acc.id} value={acc.Id}> {acc.Name}</option>
                    ))}
                  </select>
                </td>
                <td className="p-2">

                  <div className="input-group input-group-sm">
                    {/* <span className="input-group-text bg-white border-end-0 text-muted">$</span> */}
                    <input
                      type="number"
                      className="form-control "
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      value={row.debit}

                      onChange={e => {
                        updateRow(index, 'debit', Number(e.target.value) || "");
                        updateRow(index, 'credit', 0);
                      }}
                      disabled={row.credit > 0}
                    />
                  </div>
                </td>
                <td className="p-2">
                  <div className="input-group input-group-sm">
                    {/* <span className="input-group-text bg-white border-end-0 text-muted">$</span> */}
                    <input
                      type="number"
                      className="form-control "
                      placeholder="0.00"
                      value={row.credit}
                      style={{ width: '100%' }}
                      onChange={e => {


                        updateRow(index, 'credit', Number(e.target.value) || "");
                        updateRow(index, 'debit', 0);
                      }}
                    // disabled={row.debit > 0}
                    />
                  </div>
                </td>
                <td className="p-2">
                  <input type="text" className={`form-control form-control-sm  bg-light
                  ${!isValid && !row.LineMemo?.length ? "border border-danger" : "border-0"
                    }
                  `} placeholder="Line description..."

                    value={row.LineMemo} onChange={e => updateRow(index, 'LineMemo', e.target.value)} />
                </td>
                <td className="text-center">
                  <button
                    className="btn btn-link text-danger p-1"
                    onClick={() => removeRow(row.id)}
                    style={{ textDecoration: 'none' }}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="fw-bold text-sm" style={{ backgroundColor: isBalanced ? '#f0fff4' : '#fff5f5' }}>
            <tr>
              <td className="text-end py-3">Totals</td>
              <td className="py-3" style={{ color: '#1e293b' }}>{formatCurrency(totalDebit)}</td>
              <td className="py-3" style={{ color: '#1e293b' }}>{formatCurrency(totalCredit)}</td>
              <td colSpan="2" className={`text-end py-3 ${isBalanced ? 'text-success' : 'text-danger'}`}>
                {isBalanced ? (
                  <span><CheckCircle size={14} className="me-1" /> Balanced</span>
                ) : (
                  <span><AlertCircle size={14} className="me-1" /> Out of Balance: {formatCurrency(difference)}</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <button
          className="btn d-flex align-items-center gap-2"
          onClick={addRow}
          style={{ backgroundColor: '#ffffff', border: '1px solid #cbd5e1', color: '#475569' }}
        >
          <PlusCircle size={16} /> Add Line
        </button>
        <div className="d-flex gap-2">
          <button className="btn" style={{ color: '#64748b' }}>Cancel</button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            disabled={!isBalanced}
            style={{
              padding: '8px 24px',
              backgroundColor: isBalanced ? '#2563eb' : '#94a3b8',
              borderColor: isBalanced ? '#2563eb' : '#94a3b8',
              boxShadow: isBalanced ? '0 4px 6px -1px rgba(37, 99, 235, 0.2)' : 'none'
            }}
            onClick={handleSubmit}
          >
            <Save size={16} /> Post Journal
          </button>
        </div>
      </div>
    </div>
  );
};


// --- Component: Reclassification Tool ---


const ReclassificationTool = ({ lederList }) => {

  const initialTrans = {
    SourceLedgerId: '',
    DestinationLedgerId: '',
    TransactionId: '',
    date: new Date().toISOString().split('T')[0],
    reason: ''
  }
  const [formData, setFormData] = useState(initialTrans);

  const [isLoading, setIsLoading] = useState(false)

  const [transectionList, setTransectionList] = useState([])

  const getTranssectionList = (id) => {

    PostCallWithErrorResponse(ApiConfig.GET_MASTER_TRANSECTION + id, JSON.stringify({})).then((res) => {

      if (res?.json?.Success) {
        setTransectionList(res?.json?.Data)
      }

    })

  }

  const handleSubmit = () => {

    setIsLoading(true)


    PostCallWithErrorResponse(ApiConfig.LEDGER_RECLASSIFICATION, JSON.stringify(formData)).then((res) => {

      if (res?.json?.Success) {

        ToastMsg('success', res?.json?.Message)

        setFormData(initialTrans)

        setTransectionList([])

      } else {
        ToastMsg('error', res?.json?.Message)
      }

      setIsLoading(false)

    })

  }



  return (
    <div className="card-std" style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
      {/* Header with Icon */}
      <div className="d-flex align-items-center gap-3 mb-4 border-bottom pb-3">
        <div
          className="p-2 rounded"
          style={{ backgroundColor: '#e0f7fa', color: '#00acc1' }}
        >
          <RefreshCcw size={24} />
        </div>
        <div>
          <h4 className="mb-1" style={{ color: '#1e293b' }}>Expense Reclassification</h4>
          <p className="text-muted text-sm mb-0">Move costs from one GL account to another.</p>
        </div>
      </div>

      <div className="row g-4">
        {/* Source - Left Col (Credit/Decrease) */}
        <div className="col-lg-4">
          <div
            className="p-4 border rounded h-100 d-flex flex-column"
            style={{ backgroundColor: '#fff5f5', borderColor: '#feb2b2' }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="text-xs text-uppercase fw-bold mb-0" style={{ color: '#c53030' }}>Move From (Credit)</h6>
              <span
                className="badge border"
                style={{ backgroundColor: '#ffffff', color: '#c53030' }}
              >
                - CR
              </span>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium text-sm">Source Account</label>
              <select
                className="form-select bg-white"
                value={formData.SourceLedgerId}
                onChange={e => {

                  getTranssectionList(Number(e.target.value))
                  setFormData({ ...formData, SourceLedgerId: Number(e.target.value) })
                }}
              >
                <option value="">Select Account...</option>
                {lederList.map((acc, index) => (
                  <option key={index} value={acc.Id}> {acc.Name} - {acc?.PrimaryGroup}</option>
                ))}
              </select>
            </div>
            <div className="mt-auto">
              <div
                className="py-2 px-3 text-xs d-flex gap-2 mb-0 rounded"
                style={{ backgroundColor: 'rgba(197, 48, 48, 0.1)', color: '#c53030' }}
              >
                <div className="fw-bold">Impact:</div> Decreases balance.
              </div>
            </div>
          </div>
        </div>

        {/* Destination - Middle Col (Debit/Increase) */}
        <div className="col-lg-4">
          <div
            className="p-4 border rounded h-100 d-flex flex-column"
            style={{ backgroundColor: '#f0fff4', borderColor: '#9ae6b4' }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="text-xs text-uppercase fw-bold mb-0" style={{ color: '#2f855a' }}>Move To (Debit)</h6>
              <span
                className="badge border"
                style={{ backgroundColor: '#ffffff', color: '#2f855a' }}
              >
                + DR
              </span>
            </div>

            <div className="mb-3">
              <label className="form-label fw-medium text-sm">Destination Account</label>
              <select
                className="form-select bg-white"
                value={formData.DestinationLedgerId}
                onChange={e => setFormData({ ...formData, DestinationLedgerId: Number(e.target.value) })}
              >
                <option value="">Select Account...</option>
                {lederList.map((acc, index) => (
                  <option key={index} value={acc.Id}> {acc.Name} - {acc?.PrimaryGroup}</option>
                ))}
              </select>
            </div>
            <div className="mt-auto">
              <div
                className="py-2 px-3 text-xs d-flex gap-2 mb-0 rounded"
                style={{ backgroundColor: 'rgba(47, 133, 90, 0.1)', color: '#2f855a' }}
              >
                <div className="fw-bold">Impact:</div> Increases balance.
              </div>
            </div>
          </div>
        </div>

        {/* Details - Right Col (White/Neutral) */}
        <div className="col-lg-4">
          <div
            className="p-4 border rounded h-100 d-flex flex-column"
            style={{ backgroundColor: '#ffffff', borderColor: '#e2e8f0' }}
          >
            <h6 className="text-xs text-uppercase text-muted fw-bold mb-3">Transaction Details</h6>

            <div className="mb-3">
              <label className="form-label fw-medium text-sm">Transfer Account</label>
              <select
                className="form-select bg-white"
                value={formData.TransactionId}
                onChange={e => setFormData({ ...formData, TransactionId: Number(e.target.value) })}
              >
                <option value="">Select Account...</option>
                {transectionList.map((acc, index) => (
                  <option key={index} value={acc.TransactionId}> {`Cr ${acc?.CrAmount} - Dr ${acc?.DrAmount}`}</option>
                ))}
              </select>

              <div className="mb-4 flex-grow-1">
                <label className="form-label text-sm">Reason</label>
                <textarea
                  className="form-control form-control-sm"
                  style={{ height: '80px', resize: 'none', backgroundColor: '#fafafa' }}
                  placeholder="Explanation for audit trail..."
                  value={formData.reason}
                  onChange={e => setFormData({ ...formData, reason: e.target.value })}
                ></textarea>
              </div>
            </div>



            <div className="d-flex gap-2 mt-auto">
              <button
                className="btn btn-sm btn-outline-secondary w-50"
                onClick={() => setFormData({ SourceLedgerId: '', DestinationLedgerId: '', TransactionId: '', date: new Date().toISOString().split('T')[0], reason: '' })}
              >
                Clear
              </button>
              <button
                className="btn btn-sm btn-primary w-50"
                disabled={(!formData.TransactionId || !formData.SourceLedgerId || !formData.DestinationLedgerId) || isLoading}
                style={{
                  boxShadow: formData.TransactionId && formData.SourceLedgerId && formData.DestinationLedgerId ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                }}
                onClick={handleSubmit}
              >
                Process
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Component: Reclassification Tool ---





// --- Main AccountServiceReport Shell ---


const AccountServiceReport = () => {
  const [activeTab, setActiveTab] = useState('pl'); // Default to Profit & Loss
  const [dateRange, setDateRange] = useState({ start: '2024-01-01', end: '2024-12-31' });
  const { sidebar, userData, permissions } = useContext(AppContext);

  const tabs = [
    { id: 'pl', label: 'Profit & Loss', hasPermission: permissions && permissions['View Profit & Loss'] },
    { id: 'bs', label: 'Balance Sheet', hasPermission: permissions && permissions['View Balance Sheet'] },
    { id: 'cf', label: 'Cash Flows', hasPermission: permissions && permissions['View Cash Flows'] },
    { id: 'tb', label: 'Trial Balance', hasPermission: permissions && permissions['View Trial Balance'] },
    { id: 'gl_manual', label: 'ManualLedgerForm', hasPermission: permissions && permissions['View Manual Ledger Form'] },
    { id: 'gl_reclass', label: 'ReclassificationTool', hasPermission: permissions && permissions['View ReclassificationTool'] }
  ];

  const [lederList, setLedgerList] = useState([])




  const getLedgerList = () => {

    simpleGetCallWithErrorResponse(ApiConfig.GET_LEDGER_LIST).then((res) => {

      if (res?.json?.Success) {

        setLedgerList(res?.json?.Data)

      }

    })


  }

  const handleActiveTab = () => {

    if (permissions && !permissions['View Profit & Loss']) {
      if (permissions['View Balance Sheet']) {
        setActiveTab('bs')
      } else if (permissions['View Cash Flows']) {
        setActiveTab('cf')
      } else if (permissions['View Trial Balance']) {
        setActiveTab('tb')
      } else if (permissions['View Manual Ledger Form']) {
        setActiveTab('gl_manual')
      } else if (permissions['View ReclassificationTool']) {
        setActiveTab('gl_reclass')
      }
    }


  }

  useEffect(() => {

    handleActiveTab()

    getLedgerList()

  }, [])

  const renderContent = () => {
    switch (activeTab) {
      case 'pl': return <ProfitAndLoss dateRange={dateRange} />;
      case 'bs': return <BalanceSheet dateRange={dateRange} />;
      case 'cf': return <CashFlow dateRange={dateRange} />;
      case 'tb': return <TrialBalance dateRange={dateRange} />;
      case 'gl_manual': return <ManualLedgerForm lederList={lederList} />;
      case 'gl_reclass': return <ReclassificationTool lederList={lederList} />;
      default: return <ProfitAndLoss dateRange={dateRange} />;
    }
  };

  return (
    <main className={sidebar ? "taskMain" : "cx-active taskMain"} id="cx-main">
      <style>{STYLES}</style>
      <div id="sales-main">
        {/* --- Header Section (Matching Screenshot) --- */}
        <section className="cx-inneer-header">
          <div className="main-inner">
            <div className="left">
              <label>Account Service Report</label>
            </div>
          </div>
        </section>

        {/* --- Filters Section (Matching Dropdowns in Screenshot) --- */}


        {/* --- Report Tabs --- */}
        <section className="">
          <div className="ds-tabs-header  mt-4 mb-4">
            {tabs?.filter((i) => i?.hasPermission).map(tab => (
              <div
                key={tab.id}
                className={`ds-tab-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </div>
            ))}
          </div>

          {/* --- Main Report Render Area --- */}
          <div className="content-area-wrapper">
            {renderContent()}
          </div>
        </section>
      </div>
    </main>
  );
};


export default AccountServiceReport;

